import { useContext, useId } from 'react';

import { LinearGradient } from '@visx/gradient';
import { type AxisScale, DataContext, type SeriesProps, AreaSeries as VisxAreaSeries } from '@visx/xychart';

import type { AreaProps } from '@visx/shape';

export const AreaSeries = <XScale extends AxisScale, YScale extends AxisScale, Datum extends object>({
  variant = 'gradient',
  color,
  curve,
  data,
  dataKey,
  xAccessor,
  yAccessor,
  'data-testid': dataTestId,
}: Pick<SeriesProps<XScale, YScale, Datum>, 'data' | 'dataKey' | 'xAccessor' | 'yAccessor'> & {
  variant?: 'gradient' | 'solid';
  color?: string;
  curve?: AreaProps<Datum>['curve'];
  'data-testid'?: string;
}) => {
  const gradientId = useId();
  const context = useContext(DataContext);
  const seriesColor = color ?? context?.theme?.colors?.[0];

  // Common props for all variants
  const commonProps = {
    data,
    dataKey,
    xAccessor,
    yAccessor,
    curve,
    'data-testid': dataTestId,
  };

  // Solid variant
  if (variant === 'solid') {
    return (
      <VisxAreaSeries
        {...commonProps}
        fill={seriesColor}
        fillOpacity={1}
        lineProps={{ stroke: seriesColor, strokeWidth: 0 }}
      />
    );
  }

  // Default: gradient variant
  return (
    <>
      <LinearGradient id={gradientId} from={seriesColor} to={seriesColor} fromOpacity={0.6} toOpacity={0.0} />
      <VisxAreaSeries {...commonProps} fill={`url(#${gradientId})`} lineProps={{ stroke: seriesColor }} />
    </>
  );
};
